/**
 * Error Message Utilities
 * 
 * Provides user-friendly error messages for API errors and feature unavailability
 */

import { getApiKeyMessage } from '../services/featureAvailability/featureAvailabilityService';

export interface ErrorContext {
  featureName?: string;
  apiEndpoint?: string;
  statusCode?: number;
}

/**
 * Get user-friendly error message from an error
 * 
 * @param error - The error object or message
 * @param context - Additional context about the error (feature name, API endpoint, etc.)
 * @returns User-friendly error message string
 */
export function getUserFriendlyErrorMessage(
  error: Error | string | unknown,
  context?: ErrorContext
): string {
  // Extract error message
  let errorMessage: string;
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else {
    errorMessage = 'An unexpected error occurred';
  }

  // Check for common error patterns and provide user-friendly messages
  const lowerMessage = errorMessage.toLowerCase();

  // Network/connection errors
  if (lowerMessage.includes('failed to fetch') || 
      lowerMessage.includes('networkerror') ||
      lowerMessage.includes('network error') ||
      lowerMessage.includes('connection refused')) {
    
    if (context?.featureName === 'simulations' || context?.apiEndpoint?.includes('/conductor/')) {
      return 'Unable to connect to simulation backend. Please ensure you are connected to Tailscale and that REACT_APP_API_BASE_URL is configured correctly.';
    }
    
    if (context?.apiEndpoint?.includes('/api/population-grid')) {
      return 'Unable to connect to population analysis backend. Please check your network connection.';
    }
    
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  // Authentication errors
  if (lowerMessage.includes('401') || 
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('authentication') ||
      lowerMessage.includes('not authenticated')) {
    return 'Authentication required. Please log in and try again.';
  }

  // API key missing errors
  if (lowerMessage.includes('api key') || 
      lowerMessage.includes('not configured') ||
      lowerMessage.includes('missing required')) {
    
    if (context?.featureName) {
      // Use feature availability service to get specific message
      const apiKeyMessages: Record<string, string> = {
        simulations: getApiKeyMessage('REACT_APP_API_BASE_URL'),
        map_save_load: getApiKeyMessage('REACT_APP_API_BASE_URL'),
        graphhopper_routing: getApiKeyMessage('GRAPHHOPPER_API_KEY'),
        here_routing: getApiKeyMessage('HERE_API_KEY'),
        openroute_routing: getApiKeyMessage('REACT_APP_OPENROUTE_API_KEY'),
        jira_integration: getApiKeyMessage('jira_integration'),
        ai_chat_agent: getApiKeyMessage('OPENAI_API_KEY'),
        ai_fleet_generator: getApiKeyMessage('OPENAI_API_KEY'),
        ai_trip_generator: getApiKeyMessage('OPENAI_API_KEY'),
      };
      
      return apiKeyMessages[context.featureName] || errorMessage;
    }
    
    return 'This feature requires an API key. Please configure the required API key in your environment.';
  }

  // Backend unavailable errors
  if (lowerMessage.includes('503') || 
      lowerMessage.includes('service unavailable') ||
      lowerMessage.includes('not configured')) {
    
    if (context?.featureName === 'simulations') {
      return 'Simulation backend is not available. Connect to Tailscale or ensure REACT_APP_API_BASE_URL is configured.';
    }
    
    return 'The service is temporarily unavailable. Please try again later.';
  }

  // Timeout errors
  if (lowerMessage.includes('timeout') || 
      lowerMessage.includes('timed out')) {
    return 'The request timed out. This may be due to network issues or the server being busy. Please try again.';
  }

  // Not found errors
  if (lowerMessage.includes('404') || 
      lowerMessage.includes('not found')) {
    if (context?.apiEndpoint?.includes('/route')) {
      return 'No route found between the specified locations. The routing constraints may be too restrictive.';
    }
    return 'The requested resource was not found. Please check your input and try again.';
  }

  // Server errors
  if (lowerMessage.includes('500') || 
      lowerMessage.includes('internal server error')) {
    return 'A server error occurred. Please try again later. If the problem persists, contact support.';
  }

  // Tailscale/backend connection errors
  if (lowerMessage.includes('tailscale') || 
      lowerMessage.includes('backend connection')) {
    return 'Backend connection required. Connect to Tailscale or ensure the backend URL is configured and accessible.';
  }

  // Default: return original message if we can't provide a better one
  return errorMessage;
}

/**
 * Check if an error is due to missing API key or backend connection
 * 
 * @param error - The error object or message
 * @param context - Additional context about the error
 * @returns True if error is due to missing API key or backend
 */
export function isApiUnavailableError(
  error: Error | string | unknown,
  context?: ErrorContext
): boolean {
  const message = getUserFriendlyErrorMessage(error, context).toLowerCase();
  
  return message.includes('api key') ||
         message.includes('not configured') ||
         message.includes('tailscale') ||
         message.includes('backend connection') ||
         message.includes('not available');
}

/**
 * Check if an error is due to authentication requirements
 * 
 * @param error - The error object or message
 * @param context - Additional context about the error
 * @returns True if error is due to authentication
 */
export function isAuthenticationError(
  error: Error | string | unknown,
  context?: ErrorContext
): boolean {
  const message = getUserFriendlyErrorMessage(error, context).toLowerCase();
  
  return message.includes('authentication') ||
         message.includes('unauthorized') ||
         message.includes('401') ||
         message.includes('not authenticated') ||
         message.includes('log in');
}

